import {
  type InferFullRequest,
  cancelTypedQueries,
  getTypedQueriesData,
  invalidateTypedQueries,
  setTypedQueryData,
  useApiClient,
} from '@/hooks/useApiClient'
import type { components, paths } from '@/types/api/v1'
import { getCurrentMarket } from '@/utils/general'
import { convertUndefinedToNull } from '@/utils/object'
import {
  type QueryClient,
  type UseMutationOptions,
  type UseQueryResult,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query'
import type { FetchResponse, MaybeOptionalInit } from 'openapi-fetch'
import { useMemo } from 'react'

export type DriverOverviewRow = components['schemas']['DriverInfoRow']

type DriverUpsertRequest = InferFullRequest<'post', '/driver/upsert'>
type DriverRequest = components['schemas']['DriverRequest']

type SetPayConfigRequest = MaybeOptionalInit<paths['/driver/set_pay_configuration'], 'post'>
type SetPayConfigResponse = Required<
  FetchResponse<paths['/driver/set_pay_configuration']['post'], SetPayConfigRequest, 'application/json'>
>

type DriverMetadataResponse = {
  trailers: string[]
  tractors: string[]
  templates: {
    days: components['schemas']['ScheduleDay'][]
    description?: string | null
    id: string
    name: string
    offset_day?: number | null
  }
  depots: string[]
  carrier_depots: { name: string; carrier: string }[]
}

export const formatDriverForUpdate = (driver: DriverOverviewRow): DriverUpsertRequest => ({
  body: convertUndefinedToNull({
    ...driver,
    name: driver.name ?? '',
    username: driver.username ?? '',
    active: driver.status === 'ACTIVE',
    shift_preference: driver.shift_preference ?? 'pm',
    am_shift_start_time: driver.am_shift?.start_time,
    pm_shift_start_time: driver.pm_shift?.start_time,
    shift_length_hours: driver.shift_length?.shift_length_hours,
    tractor_id: null,
    trailer_id: null,
  }),
})

export const useDriverManagement = () => {
  const api = useApiClient()
  const queryClient = useQueryClient()

  const useDriverOverview = ({
    onlyActiveDrivers,
    includeCarriers = false,
  }: { onlyActiveDrivers?: boolean; includeCarriers?: boolean }) =>
    api.useQuery(
      'post',
      '/driver/info/overview',
      {
        body: {
          market: getCurrentMarket() ?? undefined,
          include_carriers: includeCarriers,
        },
      },
      {
        select: (data) => (onlyActiveDrivers ? data.rows.filter((x) => x.status === 'ACTIVE') : data.rows),
      }
    )

  const useDriverTemplates = () => api.useQuery('post', '/driver_schedule/template/overview')

  const useDriverUpdate = () =>
    api.useMutation('post', '/driver/upsert', {
      onMutate: async ({ body }) => onDriverUpdate(queryClient, body),
      onError: (_err, _, context) =>
        onDriverUpdateError(queryClient, context as Awaited<ReturnType<typeof onDriverUpdate>>),
      onSuccess: () => invalidateTypedQueries(queryClient, 'post', '/driver/info/overview'),
    })

  const useIsUpdatingDriver = (id?: string) => {
    const mutations = useMutationState({
      filters: {
        mutationKey: ['post', '/driver/upsert'],
        status: 'pending',
        predicate: (mutation) => (mutation.state.variables as DriverUpsertRequest).body.id === id,
      },
    })
    return useMemo(() => mutations.length > 0, [mutations.length])
  }

  const useDriverMetadata = () =>
    api.useQuery(
      'post',
      '/driver/metadata',
      { params: { query: { market: getCurrentMarket() } } },
      { staleTime: 60 * 1000 }
    ) as UseQueryResult<
      DriverMetadataResponse,
      {
        detail?:
          | {
              loc: (string | number)[]
              msg: string
              type: string
            }[]
          | undefined
      }
    >

  const useDriverPayConfigUpdate = (id?: string) =>
    api.useMutation('post', '/driver/set_pay_configuration', {
      mutationKey: ['post', '/driver/set_pay_configuration', id],
      onSettled: () => invalidateTypedQueries(queryClient, 'post', '/driver/info/overview'),
    } as Omit<
      UseMutationOptions<SetPayConfigResponse['data'], SetPayConfigResponse['error'], SetPayConfigRequest>,
      'mutationKey' | 'mutationFn'
    >)

  const useUpsertTemplate = () =>
    api.useMutation('post', '/driver_schedule/template/upsert', {
      onSuccess: () => invalidateTypedQueries(queryClient, 'post', '/driver_schedule/template/overview'),
    })

  const useDeleteTemplate = () =>
    api.useMutation('post', '/driver_schedule/template/delete', {
      onSuccess: () => invalidateTypedQueries(queryClient, 'post', '/driver_schedule/template/overview'),
    })

  const useDriverTemplateUpdate = () => api.useMutation('post', '/driver/template/update')

  const useDriverByIdQuery = (id?: string) =>
    api.useQuery(
      'post',
      '/driver/info/overview',
      {
        body: {
          include_carriers: false,
        },
      },
      {
        select: (data) => data.rows.find((x) => x.id === id),
        enabled: !!id,
      }
    )

  return {
    useDriverOverview,
    useDriverByIdQuery,
    useDriverMetadata,
    useDriverTemplates,
    useDriverUpdate,
    useIsUpdatingDriver,
    useDriverPayConfigUpdate,
    useDeleteTemplate,
    useUpsertTemplate,
    useDriverTemplateUpdate,
  }
}

const onDriverUpdate = async (queryClient: QueryClient, body: DriverRequest) => {
  await cancelTypedQueries(queryClient, 'post', '/driver/info/overview')
  const previous = getTypedQueriesData(queryClient, 'post', '/driver/info/overview')
  return previous
    .filter((x) => x[1] !== undefined)
    .map(([queryKey, { row_count, rows }]) => {
      const newRows = {
        row_count: row_count,
        rows: rows.map((row) =>
          row.id === body.id ? { ...row, ...body, id: row.id, status: body.status ?? row.status } : row
        ),
      }
      setTypedQueryData(queryClient, 'post', '/driver/info/overview', { init: queryKey[2] }, newRows)
      return { init: queryKey[2], previous: rows, current: newRows }
    })
}

const onDriverUpdateError = (queryClient: QueryClient, context: Awaited<ReturnType<typeof onDriverUpdate>>) => {
  context.forEach(({ init, previous }) =>
    setTypedQueryData(
      queryClient,
      'post',
      '/driver/info/overview',
      { init },
      { row_count: previous.length, rows: previous }
    )
  )
}
